import { useState, useEffect, useCallback } from 'react';
import { HeadacheFormData, TensionFormData } from '../types';

interface OfflineData {
  id: string;
  type: 'headache' | 'tension';
  data: HeadacheFormData | TensionFormData;
  timestamp: number;
  synced: boolean;
}

interface UseOfflineSyncReturn {
  isOnline: boolean;
  pendingSync: OfflineData[];
  addOfflineData: (type: 'headache' | 'tension', data: HeadacheFormData | TensionFormData) => string;
  syncPendingData: () => Promise<void>;
  removeSyncedData: (id: string) => void;
  hasPendingData: boolean;
}

const OFFLINE_STORAGE_KEY = 'maldolor_offline_data';

export const useOfflineSync = (): UseOfflineSyncReturn => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState<OfflineData[]>([]);

  // Cargar datos pendientes del localStorage al inicializar
  useEffect(() => {
    const loadPendingData = () => {
      try {
        const storedData = localStorage.getItem(OFFLINE_STORAGE_KEY);
        if (storedData) {
          const parsed = JSON.parse(storedData) as OfflineData[];
          setPendingSync(parsed.filter(item => !item.synced));
        }
      } catch (error) {
        console.error('Error cargando datos offline:', error);
      }
    };

    loadPendingData();
  }, []);

  // Escuchar cambios de conectividad
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Conectado - iniciando sincronización automática');
      setIsOnline(true);
      // Auto-sincronizar cuando vuelva la conexión
      setTimeout(() => {
        syncPendingData();
      }, 1000);
    };

    const handleOffline = () => {
      console.log('📴 Sin conexión - modo offline activado');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Guardar datos offline
  const addOfflineData = useCallback((type: 'headache' | 'tension', data: HeadacheFormData | TensionFormData): string => {
    const offlineItem: OfflineData = {
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      synced: false
    };

    setPendingSync(prev => {
      const updated = [...prev, offlineItem];
      // Guardar en localStorage
      try {
        localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Error guardando datos offline:', error);
      }
      return updated;
    });

    console.log(`💾 Datos guardados offline: ${type}`, offlineItem);
    return offlineItem.id;
  }, []);

  // Sincronizar datos pendientes
  const syncPendingData = useCallback(async () => {
    if (!isOnline || pendingSync.length === 0) {
      return;
    }

    console.log(`🔄 Iniciando sincronización de ${pendingSync.length} elementos...`);
    
    const { headachesAPI, tensionsAPI } = await import('../services/api');
    
    let syncedCount = 0;
    const failedSync: OfflineData[] = [];

    for (const item of pendingSync) {
      try {
        if (item.type === 'headache') {
          await headachesAPI.create(item.data as HeadacheFormData);
        } else if (item.type === 'tension') {
          await tensionsAPI.create(item.data as TensionFormData);
        }
        
        // Marcar como sincronizado
        item.synced = true;
        syncedCount++;
        console.log(`✅ Sincronizado: ${item.type} del ${new Date(item.timestamp).toLocaleString()}`);
        
      } catch (error) {
        console.error(`❌ Error sincronizando ${item.type}:`, error);
        failedSync.push(item);
      }
    }

    // Actualizar estado y localStorage
    setPendingSync(failedSync);
    try {
      if (failedSync.length === 0) {
        localStorage.removeItem(OFFLINE_STORAGE_KEY);
      } else {
        localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(failedSync));
      }
    } catch (error) {
      console.error('Error actualizando localStorage:', error);
    }

    if (syncedCount > 0) {
      console.log(`🎉 Sincronización completada: ${syncedCount} elementos sincronizados`);
      
      // Mostrar notificación de éxito
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('MalDolor - Sincronización', {
          body: `${syncedCount} registro${syncedCount > 1 ? 's' : ''} sincronizado${syncedCount > 1 ? 's' : ''} correctamente`,
          icon: '/logo192.png'
        });
      }
    }

    if (failedSync.length > 0) {
      console.warn(`⚠️ ${failedSync.length} elementos no pudieron sincronizarse`);
    }

  }, [isOnline, pendingSync]);

  // Remover datos ya sincronizados
  const removeSyncedData = useCallback((id: string) => {
    setPendingSync(prev => {
      const updated = prev.filter(item => item.id !== id);
      try {
        if (updated.length === 0) {
          localStorage.removeItem(OFFLINE_STORAGE_KEY);
        } else {
          localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(updated));
        }
      } catch (error) {
        console.error('Error actualizando localStorage:', error);
      }
      return updated;
    });
  }, []);

  // Auto-sincronizar cuando vuelva la conexión
  useEffect(() => {
    if (isOnline && pendingSync.length > 0) {
      const timeoutId = setTimeout(() => {
        syncPendingData();
      }, 2000); // Esperar 2 segundos después de recuperar conexión

      return () => clearTimeout(timeoutId);
    }
  }, [isOnline, pendingSync.length, syncPendingData]);

  return {
    isOnline,
    pendingSync,
    addOfflineData,
    syncPendingData,
    removeSyncedData,
    hasPendingData: pendingSync.length > 0
  };
};